import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { makeStyles, mergeClasses, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger } from "@fluentui/react-components";
import { ArrowRight16Regular, ChevronRight20Regular } from "@fluentui/react-icons";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DesktopArea from "./DesktopArea";
import MenuHeader from "./MenuHeader";
import { dolphinTokens } from "../../../utils/Theme";
import INavigationArea from "../../../models/navigation/INavigationArea";
import INavigationSection from "../../../models/navigation/INavigationSection";
import INavigationLink from "../../../models/navigation/INavigationLink";

export interface IMegaLink {
    to: string;
    label: string;
}

const hrefFor = (area: INavigationArea, path: string) => (area.slug ? `/${area.slug}/${path}` : `/${path}`);

export interface IMegaPromo extends IMegaLink {
    tag: string;
}

interface IDesktopMegaAreaProps {
    area: INavigationArea;
    allLink?: IMegaLink;
    promo?: IMegaPromo;
}

type MegaSection = INavigationSection & {
    description?: string;
    path?: string;
    groups?: { heading: string; links: INavigationLink[] }[];
};

const accents = [
    { bg: dolphinTokens.colorLightBlue, fg: dolphinTokens.colorNeutralForegroundOnBrand },
    { bg: dolphinTokens.colorSkyLavender, fg: dolphinTokens.colorDarkBlue },
    { bg: dolphinTokens.colorMintGreen, fg: dolphinTokens.colorDarkBlue },
    { bg: dolphinTokens.colorPeachOrange, fg: dolphinTokens.colorDarkBlue },
    { bg: dolphinTokens.colorSunflowerYellow, fg: dolphinTokens.colorDarkBlue },
];

const accentStyle = (index: number) =>
    ({
        "--mega-accent": accents[index % accents.length].bg,
        "--mega-on": accents[index % accents.length].fg,
    }) as CSSProperties;

const useStyles = makeStyles({
    sheet: {
        padding: 0,
        border: "none",
        borderRadius: "22px",
        overflow: "hidden",
        width: "min(1132px, calc(100vw - 48px))",
        maxWidth: "none",
        backgroundColor: dolphinTokens.colorNeutralBackground1,
        boxShadow:
            "0 4px 14px rgba(0, 0, 0, 0.45), 0 32px 80px rgba(0, 25, 35, 0.4), inset 0 0 0 1px rgba(255, 255, 255, 0.14)",
    },
    menu: {
        display: "grid",
        gridTemplateColumns: "360px minmax(0, 1fr)",
        alignItems: "stretch",
        padding: 0,
        gap: 0,
    },

    item: {
        maxWidth: "none",
        minHeight: "unset",
        alignItems: "center",
        padding: 0,
    },

    // ---- rail ----
    rail: {
        display: "flex",
        flexDirection: "column",
        rowGap: "4px",
        padding: "18px 16px 16px",
        backgroundColor: dolphinTokens.colorDarkBlue,
        backgroundImage: `radial-gradient(ellipse 120% 60% at 15% 0%, ${dolphinTokens.colorLightBlue60}, transparent 65%)`,
    },
    railItem: {
        minHeight: "unset",
        padding: "12px 14px",
        borderRadius: "14px",
        backgroundColor: "transparent",
        color: "rgba(255, 255, 255, 0.9)",
        "--colorStrokeFocus2": "rgba(255, 255, 255, 0.8)",
        fontSize: "17px",
        fontWeight: 600,
        ":hover": { backgroundColor: "rgba(255, 255, 255, 0.08)", color: "#ffffff" },
        "[data-fui-focus-visible]": { backgroundColor: "rgba(255, 255, 255, 0.08)", color: "#ffffff" },
        ":hover:active": { backgroundColor: "rgba(255, 255, 255, 0.12)", color: "#ffffff" },
    },
    railItemActive: {
        backgroundColor: "var(--mega-accent)",
        color: "var(--mega-on)",
        ":hover": { backgroundColor: "var(--mega-accent)", color: "var(--mega-on)" },
        "[data-fui-focus-visible]": { backgroundColor: "var(--mega-accent)", color: "var(--mega-on)" },
        ":hover:active": { backgroundColor: "var(--mega-accent)", color: "var(--mega-on)" },
    },
    railInner: {
        display: "flex",
        alignItems: "center",
        columnGap: "14px",
        width: "100%",
    },
    bubble: {
        width: "12px",
        height: "12px",
        flexShrink: 0,
        borderRadius: "50%",
        backgroundColor: "var(--mega-accent)",
        boxShadow: "0 0 0 4px rgba(255, 255, 255, 0.1)",
    },
    bubbleActive: {
        backgroundColor: "currentColor",
        boxShadow: "none",
    },
    railChevron: {
        position: "absolute",
        right: "14px",
        top: "50%",
        marginTop: "-10px",
        opacity: 0,
    },
    railChevronActive: {
        opacity: 1,
    },
    railFooter: {
        marginTop: "auto",
        display: "flex",
        flexDirection: "column",
        paddingTop: "8px",
    },
    railAll: {
        minHeight: "unset",
        padding: 0,
        borderRadius: 0,
        backgroundColor: "transparent",
        color: "rgba(255, 255, 255, 0.75)",
        "--colorStrokeFocus2": "rgba(255, 255, 255, 0.8)",
        borderTop: "1px solid rgba(255, 255, 255, 0.16)",
        ":hover": { backgroundColor: "transparent", color: "#ffffff" },
        "[data-fui-focus-visible]": { backgroundColor: "transparent", color: "#ffffff" },
        ":hover:active": { backgroundColor: "transparent", color: "#ffffff" },
    },
    railAllLink: {
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "14px 14px 12px",
        fontSize: "14.5px",
        fontWeight: 500,
        color: "inherit",
        textDecorationLine: "none",
    },
    promo: {
        minHeight: "unset",
        marginTop: "10px",
        padding: 0,
        borderRadius: "14px",
        backgroundColor: "rgba(255, 255, 255, 0.07)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        color: "#ffffff",
        "--colorStrokeFocus2": "rgba(255, 255, 255, 0.8)",
        ":hover": { backgroundColor: "rgba(255, 255, 255, 0.13)", color: "#ffffff" },
        "[data-fui-focus-visible]": { backgroundColor: "rgba(255, 255, 255, 0.13)", color: "#ffffff" },
        ":hover:active": { backgroundColor: "rgba(255, 255, 255, 0.18)", color: "#ffffff" },
    },
    promoLink: {
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        columnGap: "12px",
        width: "100%",
        padding: "14px 16px",
        color: "inherit",
        textDecorationLine: "none",
    },
    promoText: {
        display: "flex",
        flexDirection: "column",
        rowGap: "2px",
    },
    promoTag: {
        fontSize: "12.5px",
        fontWeight: 600,
        color: dolphinTokens.colorMintGreen,
    },
    promoTitle: {
        fontSize: "14.5px",
        fontWeight: 600,
        lineHeight: 1.3,
    },
    promoArrow: {
        marginLeft: "auto",
        flexShrink: 0,
        color: dolphinTokens.colorMintGreen,
    },

    // ---- pane ----
    pane: {
        position: "relative",
        minWidth: 0,
        padding: "36px 44px 140px",
        backgroundColor: dolphinTokens.colorNeutralBackground1,
        color: dolphinTokens.colorDarkBlue,
    },
    lead: {
        margin: "0 0 28px",
        maxWidth: "17em",
        fontSize: "26px",
        lineHeight: 1.28,
        fontWeight: 600,
        letterSpacing: "-0.01em",
    },
    links: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        columnGap: "36px",
    },
    groupHeading: {
        gridColumn: "1 / -1",
        marginTop: "18px",
        marginBottom: "2px",
        color: dolphinTokens.colorLightBlue,
        fontSize: "0.75rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.02em",
        ":first-child": { marginTop: 0 },
    },
    empty: {
        maxWidth: "26em",
        color: dolphinTokens.colorDarkBlue60,
        fontSize: "14px",
        lineHeight: 1.6,
    },
    emptyLink: {
        color: dolphinTokens.colorLightBlue,
        fontWeight: 600,
    },
    linkItem: {
        minHeight: "unset",
        padding: 0,
        borderRadius: 0,
        backgroundColor: "transparent",
        color: dolphinTokens.colorDarkBlue,
        borderBottom: `1px solid ${dolphinTokens.colorDarkBlue20}`,
        "--colorStrokeFocus2": dolphinTokens.colorLightBlue,
        ":hover": { backgroundColor: "transparent", color: dolphinTokens.colorLightBlue },
        "[data-fui-focus-visible]": { backgroundColor: "transparent", color: dolphinTokens.colorLightBlue },
        ":hover:active": { backgroundColor: "transparent", color: dolphinTokens.colorLightBlue },
    },
    linkAnchor: {
        boxSizing: "border-box",
        display: "block",
        width: "100%",
        padding: "14px 0",
        fontSize: "16px",
        fontWeight: 500,
        color: "inherit",
        textDecorationLine: "none",
    },

    // ---- wave along the bottom of the pane ----
    band: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: "116px",
        color: "var(--mega-accent)",
        pointerEvents: "none",
    },
    wave: {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "block",
    },
    bandItem: {
        position: "absolute",
        left: "44px",
        bottom: "22px",
        minHeight: "unset",
        padding: 0,
        borderRadius: 0,
        backgroundColor: "transparent",
        color: "var(--mega-on)",
        "--colorStrokeFocus2": "var(--mega-on)",
        pointerEvents: "auto",
        ":hover": { backgroundColor: "transparent", color: "var(--mega-on)" },
        "[data-fui-focus-visible]": { backgroundColor: "transparent", color: "var(--mega-on)" },
        ":hover:active": { backgroundColor: "transparent", color: "var(--mega-on)" },
    },
    bandLink: {
        display: "inline-flex",
        alignItems: "center",
        columnGap: "10px",
        padding: "4px 0",
        fontSize: "16px",
        fontWeight: 600,
        color: "inherit",
        textDecorationLine: "none",
    },
});

export default function DesktopMegaArea(props: IDesktopMegaAreaProps) {
    if (props.area.sections.length < 2) {
        return <DesktopArea area={props.area} />;
    }
    return <MegaMenu {...props} />;
}

function MegaMenu(props: IDesktopMegaAreaProps) {
    const { area, allLink, promo } = props;
    const styles = useStyles();
    const { t } = useTranslation("navigation");

    const [menuOpen, setMenuOpen] = useState(false);
    const closeTimer = useRef<number | undefined>(undefined);
    const intentTimer = useRef<number | undefined>(undefined);

    const sections = useMemo(
        () => [...area.sections].sort((a, b) => a.order - b.order) as MegaSection[],
        [area.sections]
    );

    const [activeIndex, setActiveIndex] = useState(0);
    const clampedIndex = Math.min(activeIndex, Math.max(sections.length - 1, 0));
    const active = sections[clampedIndex];

    useEffect(() => {
        return () => {
            window.clearTimeout(closeTimer.current);
            window.clearTimeout(intentTimer.current);
        };
    }, []);

    if (!active) {
        return null;
    }

    const openNow = () => {
        window.clearTimeout(closeTimer.current);
        setMenuOpen(true);
    };
    const closeSoon = () => {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = window.setTimeout(() => setMenuOpen(false), 160);
    };

    const activateSoon = (index: number) => {
        window.clearTimeout(intentTimer.current);
        intentTimer.current = window.setTimeout(() => setActiveIndex(index), 90);
    };
    const cancelIntent = () => window.clearTimeout(intentTimer.current);

    const positioning = { autoSize: true as const, align: "center" as const, offset: { mainAxis: 9 }, overflowBoundaryPadding: 16 };

    const visibleLinks = active.links.filter((link) => !link.isHidden);

    return (
        <Menu positioning={positioning} open={menuOpen} onOpenChange={(e, data) => setMenuOpen(data.open)}>
            <MenuTrigger disableButtonEnhancement>
                <div onMouseEnter={openNow} onMouseLeave={closeSoon}>
                    <MenuHeader label={area.name} />
                </div>
            </MenuTrigger>
            <MenuPopover className={styles.sheet} onMouseEnter={openNow} onMouseLeave={closeSoon}>
                <MenuList className={styles.menu}>
                    <div className={styles.rail}>
                        {sections.map((section, index) => {
                            const isActive = index === clampedIndex;
                            return (
                                <MenuItem
                                    key={index}
                                    persistOnClick
                                    className={mergeClasses(styles.item, styles.railItem, isActive && styles.railItemActive)}
                                    style={accentStyle(index)}
                                    onMouseEnter={() => activateSoon(index)}
                                    onMouseLeave={cancelIntent}
                                    onFocus={() => setActiveIndex(index)}
                                    onClick={() => setActiveIndex(index)}
                                >
                                    <div className={styles.railInner}>
                                        <span className={mergeClasses(styles.bubble, isActive && styles.bubbleActive)} />
                                        <span>{section.name}</span>
                                        <ChevronRight20Regular
                                            className={mergeClasses(styles.railChevron, isActive && styles.railChevronActive)}
                                        />
                                    </div>
                                </MenuItem>
                            );
                        })}

                        {(allLink || promo) && (
                            <div className={styles.railFooter}>
                                {allLink && (
                                    <MenuItem className={mergeClasses(styles.item, styles.railAll)}>
                                        <Link to={allLink.to} className={styles.railAllLink}>
                                            {allLink.label}
                                            <ArrowRight16Regular />
                                        </Link>
                                    </MenuItem>
                                )}
                                {promo && (
                                    <MenuItem className={mergeClasses(styles.item, styles.promo)}>
                                        <Link to={promo.to} className={styles.promoLink}>
                                            <span className={styles.promoText}>
                                                <span className={styles.promoTag}>{promo.tag}</span>
                                                <span className={styles.promoTitle}>{promo.label}</span>
                                            </span>
                                            <ArrowRight16Regular className={styles.promoArrow} />
                                        </Link>
                                    </MenuItem>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles.pane} style={accentStyle(clampedIndex)}>
                        {active.description && <p className={styles.lead}>{active.description}</p>}

                        {active.groups && active.groups.length > 0 && (
                            <div className={styles.links}>
                                {active.groups.map((group) => (
                                    <Fragment key={group.heading}>
                                        <div className={styles.groupHeading}>{group.heading}</div>
                                        {group.links.map((link: INavigationLink, index: number) => (
                                            <MenuItem key={index} className={mergeClasses(styles.item, styles.linkItem)}>
                                                <Link to={hrefFor(area, link.path)} className={styles.linkAnchor}>
                                                    {link.title}
                                                </Link>
                                            </MenuItem>
                                        ))}
                                    </Fragment>
                                ))}
                            </div>
                        )}

                        {active.groups && active.groups.length === 0 && (
                            <p className={styles.empty}>
                                Nothing's set up here yet. Add pages to this area in{" "}
                                <Link to="/admin/navigation" className={styles.emptyLink}>Edit Navigation</Link>.
                            </p>
                        )}

                        {!active.groups && (
                            <div className={styles.links}>
                                {visibleLinks.map((link: INavigationLink, index: number) => (
                                    <MenuItem key={index} className={mergeClasses(styles.item, styles.linkItem)}>
                                        <Link to={hrefFor(area, link.path)} className={styles.linkAnchor}>
                                            {link.title}
                                        </Link>
                                    </MenuItem>
                                ))}
                            </div>
                        )}

                        <div className={styles.band}>
                            <svg
                                className={styles.wave}
                                viewBox="0 0 800 116"
                                preserveAspectRatio="none"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <path fill="currentColor" opacity="0.38" d="M0 40C130 8 260 10 400 36S660 72 800 32V116H0Z" />
                                <path fill="currentColor" d="M0 60C140 32 290 36 420 60S680 88 800 54V116H0Z" />
                            </svg>
                            {active.path && (
                                <MenuItem className={mergeClasses(styles.item, styles.bandItem)}>
                                    <Link to={hrefFor(area, active.path)} className={styles.bandLink}>
                                        {t("mega.explore", { name: active.name })}
                                        <ArrowRight16Regular />
                                    </Link>
                                </MenuItem>
                            )}
                        </div>
                    </div>
                </MenuList>
            </MenuPopover>
        </Menu>
    );
}